// Sounds of a map's entities: target_speaker (SP_target_speaker) and the
// start / stop sounds ET gives doors, plats and buttons (DoorSetSounds,
// SP_func_plat, SP_func_button). The server puts the files into the map
// bundle, the client plays them.

import { entityNum, entityVec, inlineModel, type Entity } from '../entities';
import type { Vec3 } from '../math';
import { MOVER_CLASSES } from './movers';
import { SoundScripts, noiseFiles } from '../soundScript';

/** Default range of sounds (SOUND_RANGE_DEFAULT). */
export const SOUND_RANGE_DEFAULT = 1250;

export interface SpeakerDef {
  entityNum: number;
  origin: Vec3;
  /** A file or a sound script name. */
  noise: string;
  /** LOOPED_ON / LOOPED_OFF: a looping sound toggled by use. */
  looped: boolean;
  /** LOOPED_ON: the loop plays from the start. */
  startOn: boolean;
  /** ACTIVATOR ("*" sounds): plays on the player that used it. */
  activator: boolean;
  /** Auto triggering: ms between plays (0 = only when used) and the random variance. */
  wait: number;
  random: number;
  /** 0..1 ("volume" 255 = 1). */
  volume: number;
  /** Distance where the sound fades out. */
  range: number;
}

/** Sounds of a binary mover's state changes (sound1to2, soundPos2, sound2to1, soundPos1). */
export interface MoverSounds {
  entityNum: number;
  /** Inline model: the sounds play at its middle (CG_SetEntitySoundPosition). */
  model: number;
  start1to2: string | null;
  reachedPos2: string | null;
  start2to1: string | null;
  reachedPos1: string | null;
}

export interface MapSoundDefs {
  speakers: SpeakerDef[];
  movers: MoverSounds[];
}

export function mapSoundDefs(entities: Entity[]): MapSoundDefs {
  const speakers: SpeakerDef[] = [];
  const movers: MoverSounds[] = [];
  entities.forEach((e, i) => {
    const cls = (e.classname ?? '').toLowerCase();
    if (cls === 'target_speaker') {
      const noise = e.noise ?? '';
      if (!noise) return;
      const flags = entityNum(e, 'spawnflags');
      const volume = entityNum(e, 'volume', 255) || 255;
      speakers.push({
        entityNum: i,
        origin: entityVec(e, 'origin'),
        noise: noise.startsWith('*') ? noise.slice(1) : noise,
        looped: (flags & 3) !== 0,
        startOn: (flags & 1) !== 0,
        activator: (flags & 8) !== 0 || noise.startsWith('*'),
        // s.frame = wait * 10, s.clientNum = random * 10: tenths of a second
        wait: Math.trunc(entityNum(e, 'wait') * 10) * 100,
        random: Math.trunc(entityNum(e, 'random') * 10) * 100,
        volume: Math.min(255, volume) / 255,
        range: entityNum(e, 'radius') || SOUND_RANGE_DEFAULT,
      });
      return;
    }
    const kind = MOVER_CLASSES[cls];
    const model = inlineModel(e);
    if (!kind || model <= 0) return;
    let s: Omit<MoverSounds, 'entityNum' | 'model'> | null = null;
    if (kind === 'door' || kind === 'doorRotating') {
      const type = entityNum(e, 'type');
      if (type) {
        const p = `sound/movers/doors/door${type}`;
        s = { start1to2: `${p}_open.wav`, reachedPos2: `${p}_endo.wav`, start2to1: `${p}_close.wav`, reachedPos1: `${p}_endc.wav` };
      }
    } else if (kind === 'plat') {
      s = { start1to2: 'sound/movers/plats/pt1_strt.wav', reachedPos2: 'sound/movers/plats/pt1_end.wav', start2to1: 'sound/movers/plats/pt1_strt.wav', reachedPos1: 'sound/movers/plats/pt1_end.wav' };
    } else if (kind === 'button') {
      s = { start1to2: 'sound/movers/switches/butn2.wav', reachedPos2: null, start2to1: null, reachedPos1: null };
    }
    if (s) movers.push({ entityNum: i, model, ...s });
  });
  return { speakers, movers };
}

/** Every sound file the map's entities can play (speaker noises resolved through the sound scripts). */
export function mapSoundFiles(defs: MapSoundDefs, scripts: SoundScripts): string[] {
  const out = new Set<string>();
  for (const sp of defs.speakers) for (const f of noiseFiles(sp.noise, scripts)) out.add(f);
  for (const m of defs.movers) for (const f of [m.start1to2, m.reachedPos2, m.start2to1, m.reachedPos1]) if (f) out.add(f);
  return [...out];
}
